import clsx from "clsx";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {YStack} from "../YStack";

const DEFAULT_SWIPE_THRESHOLD = 56;
const CENTER_TRANSLATE = -33.333333;
const NEXT_TRANSLATE = -66.666667;
const DEFAULT_TRANSITION_DURATION_MS = 300;
const DEFAULT_TRANSITION_TIMING = "cubic-bezier(0.22, 1, 0.36, 1)";

export interface SliderRenderProps<TItem> {
  item: TItem;
  index: number;
  isAnimating: boolean;
  isActive: boolean;
}

export interface SliderProps<TItem> {
  items: readonly TItem[];
  activeIndex?: number;
  className?: string;
  viewportClassName?: string;
  trackClassName?: string;
  panelClassName?: string;
  disabled?: boolean;
  swipeThreshold?: number;
  transitionDurationMs?: number;
  withDots?: boolean;
  shouldLoop?: boolean;
  canSwipeNavigate?: (direction: -1 | 1) => boolean;
  onNavigate?: (direction: -1 | 1) => void;
  onIndexChange?: (index: number, direction: -1 | 1) => void;
  onAnimationStateChange?: (isAnimating: boolean) => void;
  renderItem: (props: SliderRenderProps<TItem>) => React.ReactNode;
}

export interface SliderHandle {
  navigate: (direction: -1 | 1) => void;
}

const getWrappedIndex = (index: number, length: number) => {
  if (length === 0) {
    return 0;
  }

  return ((index % length) + length) % length;
};

const getBoundedIndex = (index: number, length: number) => {
  if (length === 0) {
    return 0;
  }

  return Math.min(Math.max(index, 0), length - 1);
};

const getResolvedIndex = (index: number, length: number, shouldLoop: boolean) => {
  return shouldLoop ? getWrappedIndex(index, length) : getBoundedIndex(index, length);
};

const getItemAtIndex = <TItem,>(items: readonly TItem[], index: number) => {
  const item = items[index];

  if (item === undefined) {
    throw new RangeError("Slider item index is out of bounds");
  }

  return item;
};

const SliderInner = <TItem,>(
  {
    items,
    activeIndex,
    className,
    viewportClassName,
    trackClassName,
    panelClassName,
    disabled = false,
    swipeThreshold = DEFAULT_SWIPE_THRESHOLD,
    transitionDurationMs = DEFAULT_TRANSITION_DURATION_MS,
    withDots,
    shouldLoop = true,
    canSwipeNavigate,
    onNavigate,
    onIndexChange,
    onAnimationStateChange,
    renderItem,
  }: SliderProps<TItem>,
  ref: React.ForwardedRef<SliderHandle>,
) => {
  const [internalActiveIndex, setInternalActiveIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [pendingDirection, setPendingDirection] = useState<-1 | 0 | 1>(0);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const dragOffsetRef = useRef(0);
  const maxDragOffsetRef = useRef<number>(Number.POSITIVE_INFINITY);
  const isAnimating = pendingDirection !== 0;
  const resolvedActiveIndex =
    items.length === 0
      ? 0
      : getResolvedIndex(activeIndex ?? internalActiveIndex, items.length, shouldLoop);

  const canNavigateInDirection = useCallback(
    (direction: -1 | 1) => {
      if (items.length <= 1) {
        return false;
      }

      if (shouldLoop) {
        return true;
      }

      if (direction === -1) {
        return resolvedActiveIndex > 0;
      }

      return resolvedActiveIndex < items.length - 1;
    },
    [items.length, resolvedActiveIndex, shouldLoop],
  );

  const displayItems = useMemo(() => {
    if (items.length === 0) {
      return [];
    }

    return [-1, 0, 1].map((offset, position) => {
      const index = getResolvedIndex(resolvedActiveIndex + offset, items.length, shouldLoop);

      return {
        item: getItemAtIndex(items, index),
        index,
        key: `${position}-${index}`,
      };
    });
  }, [items, resolvedActiveIndex, shouldLoop]);

  const syncDragOffset = (offset: number) => {
    dragOffsetRef.current = offset;
    setDragOffset(offset);
  };

  useEffect(() => {
    const element = viewportRef.current;

    if (!element) {
      return;
    }

    const updateViewportWidth = () => {
      maxDragOffsetRef.current = element.clientWidth || Number.POSITIVE_INFINITY;
    };

    updateViewportWidth();

    const resizeObserver = new ResizeObserver(updateViewportWidth);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    onAnimationStateChange?.(isAnimating);
  }, [isAnimating, onAnimationStateChange]);

  const startNavigation = (direction: -1 | 1) => {
    if (disabled || isAnimating || !canNavigateInDirection(direction)) {
      return;
    }

    setPendingDirection(direction);
    setShouldAnimate(true);
  };

  const resetToCenter = () => {
    setPendingDirection(0);
    syncDragOffset(0);
    setShouldAnimate(true);
  };

  useImperativeHandle(
    ref,
    () => ({
      navigate: (direction: -1 | 1) => {
        if (disabled || isAnimating || !canNavigateInDirection(direction)) {
          return;
        }

        setPendingDirection(direction);
        setShouldAnimate(true);
      },
    }),
    [canNavigateInDirection, disabled, isAnimating],
  );

  const handleTouchStart: React.TouchEventHandler<HTMLDivElement> = (event) => {
    if (disabled || isAnimating || items.length <= 1) {
      return;
    }

    setShouldAnimate(false);
    touchStartXRef.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchMove: React.TouchEventHandler<HTMLDivElement> = (event) => {
    if (disabled || isAnimating || items.length <= 1 || touchStartXRef.current === null) {
      return;
    }

    const currentX = event.touches[0]?.clientX;

    if (currentX === undefined) {
      return;
    }

    const delta = currentX - touchStartXRef.current;
    const maxDragOffset = maxDragOffsetRef.current;
    const minDelta = canNavigateInDirection(1) ? -maxDragOffset : 0;
    const maxDelta = canNavigateInDirection(-1) ? maxDragOffset : 0;
    const constrainedDelta = Math.max(minDelta, Math.min(maxDelta, delta));
    syncDragOffset(constrainedDelta);
  };

  const handleTouchEnd = () => {
    if (disabled || isAnimating || items.length <= 1 || touchStartXRef.current === null) {
      syncDragOffset(0);
      touchStartXRef.current = null;
      return;
    }

    const currentDragOffset = dragOffsetRef.current;

    if (currentDragOffset <= -swipeThreshold) {
      if (canNavigateInDirection(1) && (canSwipeNavigate?.(1) ?? true)) {
        startNavigation(1);
      } else {
        resetToCenter();
      }
    } else if (currentDragOffset >= swipeThreshold) {
      if (canNavigateInDirection(-1) && (canSwipeNavigate?.(-1) ?? true)) {
        startNavigation(-1);
      } else {
        resetToCenter();
      }
    } else {
      resetToCenter();
    }

    touchStartXRef.current = null;
  };

  const handleTransitionEnd: React.TransitionEventHandler<HTMLDivElement> = (event) => {
    if (event.target !== event.currentTarget || !shouldAnimate) {
      return;
    }

    if (pendingDirection !== 0) {
      const nextIndex = getResolvedIndex(
        resolvedActiveIndex + pendingDirection,
        items.length,
        shouldLoop,
      );

      if (activeIndex === undefined) {
        setInternalActiveIndex(nextIndex);
      }

      onIndexChange?.(nextIndex, pendingDirection);
      onNavigate?.(pendingDirection);
      setPendingDirection(0);
    }

    syncDragOffset(0);
    setShouldAnimate(false);
  };

  const sliderTransform =
    pendingDirection === -1
      ? "translateX(0%)"
      : pendingDirection === 1
        ? `translateX(${NEXT_TRANSLATE}%)`
        : `translateX(calc(${CENTER_TRANSLATE}% + ${dragOffset}px))`;

  const WrappingComponent = withDots ? YStack : React.Fragment;

  if (items.length === 0) {
    return null;
  }

  return (
    <WrappingComponent>
      <div className={className}>
        <div
          ref={viewportRef}
          className={clsx("overflow-hidden", viewportClassName)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          <div
            className={clsx("grid grid-cols-3", trackClassName)}
            onTransitionEnd={handleTransitionEnd}
            style={{
              width: "300%",
              transform: sliderTransform,
              transitionDuration: shouldAnimate ? `${transitionDurationMs}ms` : "0ms",
              transitionProperty: "transform",
              transitionTimingFunction: DEFAULT_TRANSITION_TIMING,
              willChange: "transform",
              height: "100%",
            }}
          >
            {displayItems.map(({item, index, key}) => (
              <div key={key} className={panelClassName}>
                {renderItem({item, index, isAnimating, isActive: index === resolvedActiveIndex})}
              </div>
            ))}
          </div>
        </div>
      </div>
      {withDots && items.length > 1 && (
        <div className="mt-2 flex justify-center gap-2">
          {items.map((item, index) => (
            <div
              key={item instanceof Object ? JSON.stringify(item) : String(item)}
              className={clsx(
                "h-1.5 w-1.5 rounded-full",
                index === resolvedActiveIndex ? "bg-primary" : "bg-inactive-red",
              )}
            />
          ))}
        </div>
      )}
    </WrappingComponent>
  );
};

export const Slider = forwardRef(SliderInner) as <TItem>(
  props: SliderProps<TItem> & {ref?: React.Ref<SliderHandle>},
) => React.ReactElement;
